import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Spin, Button } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import useProposalSquareStyles, { cardColors } from '../styles/components/proposal/ProposalSquare';
import ExploreCard from '../components/explore/ExploreCard';
import FilterSystem from '../components/filter/FilterSystem';
import competitionService from '../services/competitionService';
import { getCurrentUser } from '../services/authService';
import menuConfig from '../router/menuConfig';

const normalizeFilter = (filterConfig) => {
  if (!filterConfig) return filterConfig;
  if (Array.isArray(filterConfig.exprs)) {
    return {
      ...filterConfig,
      exprs: filterConfig.exprs.map((expr) =>
        expr?.field === 'topics' ? { ...expr, field: 'tags' } : expr
      ),
    };
  }
  return filterConfig;
};

const mergeFilters = (...filters) => {
  const seen = new Set();
  const exprs = [];

  filters.forEach((filter) => {
    if (!filter || !Array.isArray(filter.exprs)) return;
    filter.exprs.forEach((expr) => {
      if (!expr || !expr.field || !expr.op) return;
      const key = `${expr.field}|${expr.op}|${JSON.stringify(expr.values || [])}`;
      if (!seen.has(key)) {
        seen.add(key);
        exprs.push({ ...expr });
      }
    });
  });

  return exprs.length > 0 ? { exprs } : null;
};

const DEFAULT_CATEGORY_FILTER = {
  field: 'category',
  op: 'EQ',
  values: ['competition'],
};

const DEFAULT_PUBLISHED_FILTER = {
  field: 'status',
  op: 'EQ',
  values: ['published'],
};

const DEFAULT_BASE_FILTER = {
  exprs: [DEFAULT_CATEGORY_FILTER, DEFAULT_PUBLISHED_FILTER],
};

const DEFAULT_TAG_EXPR = {
  field: 'tags',
  op: 'EQ',
  values: ['ai_competition_model'],
};

const DEFAULT_TAG_EXPR_LIST = [DEFAULT_TAG_EXPR];

const extractTagExprs = (filter) => {
  if (!filter || !Array.isArray(filter.exprs)) return [];
  return filter.exprs.filter((expr) => expr?.field === 'tags');
};

const buildCombinedFilter = (userFilter, defaultTagExprs) => {
  const baseExprs = [...DEFAULT_BASE_FILTER.exprs];

  const userExprs = userFilter?.exprs ? [...userFilter.exprs] : [];
  const userTagExprs = [];

  userExprs.forEach((expr) => {
    if (!expr || !expr.field) return;
    if (expr.field === 'tags') {
      userTagExprs.push(expr);
    } else {
      baseExprs.push(expr);
    }
  });
 
  const mergedValues = Array.from(
    new Set([
      ...defaultTagExprs.flatMap((expr) => expr.values || []),
      ...userTagExprs.flatMap((expr) => expr.values || []),
    ])
  ).filter((value) => value !== undefined && value !== null);
  if (mergedValues.length > 0) {
    baseExprs.push({
      field: 'tags',
      op: 'EQ',
      values: mergedValues,
    });
  }
  return baseExprs.length > 0 ? { exprs: baseExprs } : null;
};

// Helper to map API content to card props
const mapContentToCard = (content) => {
  const fields = content.fields || {};
  
  // Handle author info
  let authorName = 'Unknown';
  let authorAvatar = '';
  
  if (content.user) {
    authorName = content.user.name || content.user.nickname || 'Unknown';
    authorAvatar = content.user.avatar || '';
  } else if (content.creator) {
    // Fallback if user object is not directly available but creator is
    authorName = content.creator.name || content.creator.nickname || 'Unknown';
    authorAvatar = content.creator.avatar || '';
  }
  
  // Process cover image URL
  let coverImage = null;
  if (fields.cover_image) {
    coverImage = fields.cover_image.startsWith('http') 
      ? fields.cover_image 
      : `/api/v2/contents/${fields.cover_image}/file`;
  }

  return {
    id: content.id,
    title: fields.title || fields.prompt || '无标题',
    description: fields.body_text || fields.response || fields.description || '',
    coverImage: coverImage,
    author: {
      name: authorName,
      avatar: authorAvatar
    },
    stats: {
      likes: content.interactions?.like_count || 0,
      is_liked: content.is_liked
    },
    tags: fields.tags || [],
    status: fields.status || content.status
  };
};

const ProposalSquarePage = () => {
  const { styles } = useProposalSquareStyles();
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [defaultTagExprs, setDefaultTagExprs] = useState(DEFAULT_TAG_EXPR_LIST);
  const [currentFilter, setCurrentFilter] = useState(null);
  const [appliedFilter, setAppliedFilter] = useState(
    buildCombinedFilter(null, DEFAULT_TAG_EXPR_LIST) || DEFAULT_BASE_FILTER
  );
  const [transitionFilter, setTransitionFilter] = useState(null);
  const [currentSort, setCurrentSort] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const observer = useRef();
  const loadingRef = useRef(false); // 使用ref来避免闭包问题

  const location = useLocation();
  const navigate = useNavigate();

  const currentUser = getCurrentUser && getCurrentUser();
  const userId = currentUser && currentUser.id ? currentUser.id : '';

  // 从URL中获取搜索参数
  const getSearchFromUrl = () => {
    const params = new URLSearchParams(location.search);
    return params.get('search') || '';
  };

  const [searchKeyword, setSearchKeyword] = useState(getSearchFromUrl());

  // 从localStorage中的菜单数据获取默认筛选条件
  const getDefaultFilterForPath = (path) => {
    try {
      // 优先从localStorage获取菜单数据
      const menuDataJson = localStorage.getItem('syntrust_menu_data');
      if (menuDataJson) {
        const menuData = JSON.parse(menuDataJson);
        for (const menu of menuData) {
          if (menu.children) {
            const menuItem = menu.children.find((child) => child.path === path);
            if (menuItem && menuItem.defaultFilter) {
              return normalizeFilter(menuItem.defaultFilter);
            }
          }
        }
      }

      // 如果localStorage中没有，则从menuConfig中获取作为fallback
      for (const menu of menuConfig) {
        if (menu.children) {
          const menuItem = menu.children.find((child) => child.path === path);
          if (menuItem && menuItem.defaultFilter) {
            return normalizeFilter(menuItem.defaultFilter);
          }
        }
      }
    } catch (error) {
      console.error('获取默认筛选条件失败:', error);
    }
    return null;
  };

  // 初始化默认筛选条件和监听路径变化
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const viewId = params.get('view_id');
    
    // 如果没有view_id参数，说明是默认菜单，应用默认筛选条件
    if (!viewId && location.pathname === '/explore/proposals') {
      const defaultFilter = getDefaultFilterForPath(location.pathname);
      const tagExprs = extractTagExprs(defaultFilter);
      const nextTagExprs = tagExprs.length > 0 ? tagExprs : DEFAULT_TAG_EXPR_LIST;
      setDefaultTagExprs(nextTagExprs);
  setTransitionFilter({ userFilter: { exprs: [] }, tagExprs: nextTagExprs });
      setCurrentSort(null);
      setPage(1);
      setProposals([]);
      setHasMore(true);
      setLoading(true);
      setIsInitialized(true);
    } else if (!isInitialized) {
      // 其他情况也需要初始化
      setIsInitialized(true);
      setLoading(true);
    }
  }, [location.pathname, location.search, isInitialized]);

  useEffect(() => {
    if (!transitionFilter) return;
    const userExprs = transitionFilter.userFilter?.exprs || [];
    const userTagExprs = userExprs.filter((expr) => expr.field === 'tags');
    const nonTagExprs = userExprs.filter((expr) => expr.field !== 'tags');

    const defaultTags = (transitionFilter.tagExprs && transitionFilter.tagExprs.length > 0)
      ? transitionFilter.tagExprs
      : DEFAULT_TAG_EXPR_LIST;

    const tagExprMap = new Map();
    [...defaultTags, ...userTagExprs].forEach((expr) => {
      if (!expr || !Array.isArray(expr.values) || expr.values.length === 0) return;
      const key = expr.values[0];
      if (!tagExprMap.has(key)) {
        tagExprMap.set(key, {
          field: 'tags',
          op: expr.op || 'EQ',
          values: [...expr.values],
        });
      }
    });

    const tagExprs = Array.from(tagExprMap.values());

    const nextFilter = {
      exprs: [
        ...DEFAULT_BASE_FILTER.exprs,
        ...nonTagExprs,
        ...tagExprs,
      ],
    };
    setAppliedFilter(nextFilter);
    setCurrentFilter(transitionFilter.userFilter);
    setDefaultTagExprs(transitionFilter.tagExprs);
    setTransitionFilter(null);
  }, [transitionFilter]);

  // 监听URL参数变化
  useEffect(() => {
    const newSearchKeyword = getSearchFromUrl();
    if (newSearchKeyword !== searchKeyword && isInitialized) {
      setSearchKeyword(newSearchKeyword);
      setPage(1); // 重置页码
      setProposals([]); // 清空列表
      setHasMore(true);
      setLoading(true); // 触发数据刷新
    }
  }, [location.search, isInitialized, searchKeyword]);

  // 处理筛选变化
  const handleFilterChange = (filterParams) => {
    const userFilter = filterParams ? normalizeFilter(filterParams) : { exprs: [] };
    setTransitionFilter({ userFilter, tagExprs: defaultTagExprs });
    setPage(1); // 重置页码
    setProposals([]); // 清空列表
    setHasMore(true);
  };

  // 处理排序变化
  const handleSortChange = (sortParams) => {
    setCurrentSort(sortParams);
    setPage(1); // 重置页码
    setProposals([]); // 清空列表
    setHasMore(true);
  };

  // 滚动加载逻辑
  const lastProposalRef = useCallback(
    (node) => {
      // 清理之前的观察器
      if (observer.current) {
        observer.current.disconnect();
      }

      // 如果没有节点或者正在加载，不设置观察器
      if (!node || loadingRef.current) {
        return;
      }

      // 创建新的观察器
      observer.current = new window.IntersectionObserver(
        (contents) => {
          const entry = contents[0];
          if (entry.isIntersecting && hasMore && !loadingRef.current) {
            loadingRef.current = true;
            setPage((prev) => prev + 1);
            setLoading(true);
          }
        },
        {
          rootMargin: '100px', // 提前100px触发
          threshold: 0.1
        }
      );

      // 开始观察
      observer.current.observe(node);
    },
    [hasMore] // 只依赖hasMore，避免频繁重建
  );

  // 同步loading状态到ref
  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  // 请求控制器引用
  const requestRef = useRef(null);

  // 加载数据 - 使用useCallback优化，避免不必要的重新创建
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      // 合并基础筛选条件、用户筛选条件与搜索条件
      const filtersToMerge = [appliedFilter];

      if (searchKeyword) {
        filtersToMerge.push({
          exprs: [
            {
              field: 'title',
              op: 'LIKE',
              values: [searchKeyword],
            },
          ],
        });
      }
      const combinedFilter = mergeFilters(...filtersToMerge);

      // 构建请求参数，符合content search API的参数格式
      const requestParams = {
        type: 'article', // 内容类型为文章
        filter: combinedFilter || undefined,
        sort: currentSort || { field: "inserted_at", desc: true }, // 默认按创建时间降序
        pagination: {
          page: page,
          per_page: 20
        }
      };

      // 取消之前的请求
      if (requestRef.current) {
        requestRef.current.abort();
      }

      // 创建新的请求
      requestRef.current = new AbortController();
      const signal = requestRef.current.signal;
      
      // 调用content search API
      const response = await competitionService.searchContents(requestParams, { signal });
      if (response && response.contents) {
        // 直接使用API返回的原始数据，只添加colorIdx用于卡片样式
        const formattedData = response.contents.map((item, idx) => ({
          ...item,
          colorIdx: (idx + ((page - 1) * 20)) % cardColors.length,
        }));

        setProposals(prev => page === 1 ? formattedData : [...prev, ...formattedData]);
        
        // 使用metadata中的分页信息判断是否还有更多数据
        if (response.metadata) {
          const { total, page: currentPage, per_page, total_pages } = response.metadata;
          setHasMore(currentPage < total_pages);
        } else {
          // 如果没有metadata，使用contents长度判断
          setHasMore(response.contents.length === 20);
        }
      } else {
        setHasMore(false);
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('获取提案列表失败:', error);
        if (page === 1) {
          setProposals([]);
        }
        setHasMore(false);
      }
    } finally {
      setLoading(false);
      loadingRef.current = false;
      requestRef.current = null;
    }
  }, [page, appliedFilter, currentSort, searchKeyword, location.pathname]);

  // 使用useEffect监听fetchData的变化
  useEffect(() => {
    if (!isInitialized) return;
    
    // 防抖处理
    const timeoutId = setTimeout(fetchData, 100);
    return () => {
      clearTimeout(timeoutId);
      if (requestRef.current) {
        requestRef.current.abort();
      }
    };
  }, [fetchData, isInitialized]);

  // 初始化数据获取
  useEffect(() => {
    // 如果还没有数据且没有加载中，则触发数据获取
    if (proposals.length === 0 && !loading && isInitialized) {
      fetchData();
    }
  }, [isInitialized]); // 只在isInitialized变化时执行

  // 外部触发刷新方法
  const refreshProposals = () => {
    setPage(1);
    setProposals([]);
    setHasMore(true);
    setTransitionFilter({ userFilter: currentFilter, tagExprs: defaultTagExprs });
  };

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.filterContainer}>
        <FilterSystem
          onFilterChange={handleFilterChange}
          onSortChange={handleSortChange}
          currentFilter={currentFilter}
          currentSort={currentSort}
          // 传递刷新方法给新建提案弹窗
          onProposalCreated={refreshProposals}
        />
      </div>

      <div className={styles.cardGrid}>
        {proposals.map((item, idx) => {
          // 如果是最后一个元素，添加ref用于滚动加载
          const isLast = idx === proposals.length - 1;
          const cardProps = mapContentToCard(item);
          return (
            <div key={item.id || idx} ref={isLast ? lastProposalRef : null}>
              <ExploreCard
                {...cardProps}
                layout="horizontal"
                onClick={() => navigate(`/explore/proposals/${item.id}`)}
              />
            </div>
          );
        })}
      </div>
      {loading && <div style={{ textAlign: 'center', padding: 32 }}><Spin /></div>}
      {!loading && proposals.length === 0 && (
        <div style={{ textAlign: 'center', color: '#aaa', padding: 32 }}>
          {searchKeyword ? (
            <div>
              <div>没有找到包含 "{searchKeyword}" 的提案</div>
              <div style={{ fontSize: '14px', marginTop: '8px' }}>尝试使用其他关键词搜索</div>
            </div>
          ) : (
            '暂无提案数据'
          )}
        </div>
      )}
      {!hasMore && proposals.length > 0 && <div style={{ textAlign: 'center', color: '#aaa', padding: 16 }}>没有更多了</div>}
    </div>
  );
};

export default ProposalSquarePage;
